import React, { useState } from "react";
import { useI18n } from "@/i18n";
import { LOGO_URL } from "@/constants";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

export default function Header() {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  const navItems = [
    { label: t.nav.home, href: "/" },
    { label: t.nav.catalog, href: "/#catalog" },
    { label: t.nav.projects, href: "/#projects" },
    { label: t.nav.reviews, href: "/#reviews" },
    { label: t.nav.contact, href: "/#contact" },
  ];

  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group" onClick={close}>
          <div className="h-10 w-10 rounded-sm overflow-hidden border border-white/10 bg-black">
            <img src={LOGO_URL} alt="All Possible 3D Solutions" className="h-full w-full object-cover" />
          </div>
          <div className="hidden sm:block leading-none">
            <div className="font-heading text-[15px] font-extrabold tracking-tight">ALL POSSIBLE</div>
            <div className="font-mono-accent text-[10px] text-brand-blue-light">3D · SOLUTIONS</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="nav-links">
          {navItems.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="font-mono-accent text-xs uppercase px-3 py-2 text-zinc-300 hover:text-white transition-colors"
              data-testid={`nav-link-${n.label.toLowerCase()}`}
            >
              {n.label}
            </a>
          ))}
          <Link
            to="/admin"
            className={`font-mono-accent text-xs uppercase px-3 py-2 transition-colors ${
              pathname.startsWith("/admin") ? "text-brand-blue-light" : "text-zinc-500 hover:text-white"
            }`}
            data-testid="nav-link-admin"
          >
            {t.nav.admin}
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <div
            role="group"
            data-testid="lang-toggle"
            className="flex items-center rounded-sm border border-white/15 overflow-hidden font-mono-accent text-[11px]"
          >
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2.5 py-1 uppercase transition-colors ${
                lang === "en" ? "bg-brand-blue text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-pt"
              onClick={() => setLang("pt")}
              className={`px-2.5 py-1 uppercase transition-colors ${
                lang === "pt" ? "bg-brand-blue text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              PT
            </button>
          </div>

          <button
            className="lg:hidden text-white p-2 border border-white/15 rounded-sm"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black/90 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {navItems.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={close}
                className="font-mono-accent text-xs uppercase py-3 text-zinc-200 border-b border-white/5"
              >
                {n.label}
              </a>
            ))}
            <Link
              to="/admin"
              onClick={close}
              className="font-mono-accent text-xs uppercase py-3 text-zinc-500"
            >
              {t.nav.admin}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
